import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  RefreshCw,
  ImageIcon,
  Upload,
  Tag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { getImageUrl } from '@/utils/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  name: '',
  logo_url: '',
  color: '#f97316',
  description: '',
  is_active: true,
};

export default function AdminBrandsPage() {
  const { token } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/brands/with-count`);
      setBrands(res.data);
    } catch (error) {
      toast.error('Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setIsDialogOpen(true);
  };

  const openEdit = (brand) => {
    setEditingId(brand.id);
    setForm({
      name: brand.name || '',
      logo_url: brand.logo_url || '',
      color: brand.color || '#f97316',
      description: brand.description || '',
      is_active: brand.is_active !== false,
    });
    setIsDialogOpen(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, logo_url: res.data.url }));
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Brand name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/brands/${editingId}`, form, authHeaders);
        toast.success('Brand updated');
      } else {
        await axios.post(`${API_URL}/api/brands`, form, authHeaders);
        toast.success('Brand created');
      }
      setIsDialogOpen(false);
      fetchBrands();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save brand');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!brandToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/brands/${brandToDelete.id}`, authHeaders);
      toast.success('Brand deleted');
      setBrands((prev) => prev.filter((b) => b.id !== brandToDelete.id));
      setBrandToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete brand');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="admin-brands-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Brands</h1>
          <p className="text-slate-600">Manage brand logos, colors and descriptions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchBrands} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button className="btn-primary" onClick={openAdd} data-testid="add-brand-button">
            <Plus className="w-4 h-4 mr-2" />
            Add Brand
          </Button>
        </div>
      </div>

      {/* Brand Grid */}
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-40 bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : brands.length === 0 ? (
        <div className="text-center py-16 text-slate-500 bg-white rounded-lg border border-slate-200">
          <Tag className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          No brands yet. Click &quot;Add Brand&quot; to create one.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.map((brand) => (
            <div
              key={brand.id}
              className="bg-white rounded-lg border border-slate-200 p-4 flex flex-col"
              data-testid={`brand-card-${brand.name}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div
                  className="w-20 h-20 rounded-lg border border-slate-100 flex items-center justify-center overflow-hidden bg-slate-50 flex-shrink-0"
                  style={{ borderTop: `3px solid ${brand.color || '#f97316'}` }}
                >
                  {brand.logo_url ? (
                    <img
                      src={getImageUrl(brand.logo_url)}
                      alt={brand.name}
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <ImageIcon className="w-7 h-7 text-slate-300" />
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => openEdit(brand)}
                    title="Edit brand"
                    data-testid={`edit-brand-${brand.name}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => setBrandToDelete(brand)}
                    title="Delete brand"
                    data-testid={`delete-brand-${brand.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-slate-900">{brand.name}</h3>
                <span
                  className="w-3 h-3 rounded-full border border-slate-200"
                  style={{ backgroundColor: brand.color || '#f97316' }}
                />
              </div>
              <p className="text-sm text-slate-500 line-clamp-2 flex-1">
                {brand.description || 'No description'}
              </p>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                <Badge variant="outline" className="text-xs">
                  {brand.count || 0} engines
                </Badge>
                {brand.is_active !== false ? (
                  <Badge className="text-xs bg-green-100 text-green-700">Active</Badge>
                ) : (
                  <Badge className="text-xs bg-slate-100 text-slate-600">Hidden</Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Brand' : 'Add Brand'}</DialogTitle>
            <DialogDescription>
              {editingId ? 'Update brand details and logo.' : 'Create a new brand.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="brand-name">Name *</Label>
              <Input
                id="brand-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Yanmar"
                className="mt-1"
                data-testid="brand-name-input"
              />
            </div>

            {/* Logo */}
            <div>
              <Label>Logo</Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="w-16 h-16 rounded-lg border border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50 flex-shrink-0">
                  {form.logo_url ? (
                    <img
                      src={getImageUrl(form.logo_url)}
                      alt="logo preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-slate-300" />
                  )}
                </div>
                <div className="flex-1">
                  <label className="inline-flex">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                      data-testid="brand-logo-file"
                    />
                    <span className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-slate-200 cursor-pointer hover:bg-slate-50">
                      {uploading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      Upload image
                    </span>
                  </label>
                </div>
              </div>
              <Input
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="or paste a logo URL"
                className="mt-2"
                data-testid="brand-logo-url-input"
              />
            </div>

            {/* Color */}
            <div>
              <Label htmlFor="brand-color">Brand Color</Label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  id="brand-color"
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="w-10 h-10 rounded border border-slate-200 cursor-pointer"
                  data-testid="brand-color-input"
                />
                <Input
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="brand-desc">Description</Label>
              <Textarea
                id="brand-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short brand description"
                rows={2}
                className="mt-1"
                data-testid="brand-description-input"
              />
            </div>

            {/* Active */}
            <div className="flex items-center gap-2">
              <Switch
                id="brand-active"
                checked={form.is_active}
                onCheckedChange={(c) => setForm({ ...form, is_active: c })}
                data-testid="brand-active-switch"
              />
              <Label htmlFor="brand-active" className="cursor-pointer">
                Active (visible on site)
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              className="btn-primary"
              onClick={handleSave}
              disabled={saving || uploading}
              data-testid="save-brand-button"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingId ? 'Save Changes' : 'Create Brand'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!brandToDelete} onOpenChange={(open) => !open && setBrandToDelete(null)}>
        <AlertDialogContent data-testid="delete-brand-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this brand?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <span className="font-medium">{brandToDelete?.name}</span> from the
              brands list. Existing engines keep their brand name. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="confirm-delete-brand-button"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Brand
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
